import type { AiChatStreamEvent } from '@/lib/ai-chat-stream'
import {
  AiChatHttpError,
  parseAiChatEventStream,
  readAiChatErrorCode,
} from '@/lib/ai-chat-stream'
import { buildApiHeaders } from '@/lib/api-client'
import { ensureFreshToken } from '@/lib/refresh-token'

const API_URL = import.meta.env.VITE_API_URL

interface StreamEnvelopeAiChatParams {
  envelopeId: string
  aiChatId: string
  message: string
  signal: AbortSignal
}

const request = (
  params: StreamEnvelopeAiChatParams,
  headers: Record<string, string>,
): Promise<Response> =>
  fetch(
    `${API_URL}/v1/ecm/envelopes/${params.envelopeId}/ai-chat/${params.aiChatId}/messages`,
    {
      method: 'POST',
      headers: {
        ...headers,
        ['Content-Type']: 'application/json',
        Accept: 'text/event-stream',
      },
      body: JSON.stringify({ message: params.message }),
      signal: params.signal,
    },
  )

async function retryWithFreshToken(
  params: StreamEnvelopeAiChatParams,
  rejected: string,
): Promise<Response> {
  let fresh: string
  try {
    fresh = await ensureFreshToken(rejected)
  } catch {
    window.location.href = '/logout'
    throw new AiChatHttpError(401)
  }
  return request(params, {
    ...buildApiHeaders(),
    authorization: `Bearer ${fresh}`,
  })
}

export async function* streamEnvelopeAiChat(
  params: StreamEnvelopeAiChatParams,
): AsyncGenerator<AiChatStreamEvent> {
  if (params.signal.aborted) return

  const headers = buildApiHeaders()
  let response = await request(params, headers)

  if (response.status === 401 && headers.authorization) {
    const rejected = headers.authorization.replace(/^Bearer /, '')
    response = await retryWithFreshToken(params, rejected)
  }

  if (!response.ok) {
    throw new AiChatHttpError(
      response.status,
      await readAiChatErrorCode(response),
    )
  }
  if (!response.body) throw new AiChatHttpError(response.status)

  yield* parseAiChatEventStream(response.body, params.signal)
}
